import React, { useState, useEffect, useMemo } from 'react';
import carnetCommandeApi from '../services/carnet-commande';
import './CarnetCommande.css';
import { afficherImmat, immatCorrespond } from '../utils/immat';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}

const STATUT_LIVRAISON = {
  livre:                { label: 'Reçu',               color: 'light-dark(#22c55e, var(--color-success-dark))', bg: '#22c55e18', order: 3 },
  livre_partiellement:  { label: 'Reçu partiellement', color: '#f59e0b', bg: '#f59e0b18', order: 2 },
  recu_partiellement:   { label: 'Reçu partiellement', color: '#f59e0b', bg: '#f59e0b18', order: 2 },
  en_commande:          { label: 'En commande',        color: 'light-dark(#3b82f6, var(--color-info-dark))', bg: '#3b82f618', order: 1 },
};

function statutBadge(statut) {
  const s = STATUT_LIVRAISON[statut] || STATUT_LIVRAISON.en_commande;
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, color: s.color, background: s.bg, border: `1px solid ${s.color}30` }}>
      {s.label}
    </span>
  );
}

export default function BackOrders({ user, rows: propRows, loading, reload, setHeaderAction }) {
  const allRows = propRows || [];
  const [search, setSearch]   = useState('');
  const [modal, setModal]     = useState(null);
  const [filtreSociete, setFiltreSociete] = useState('');

  // Resolve form state
  const [resolveForm, setResolveForm] = useState(null);
  const [resolveComment, setResolveComment] = useState('');
  const [saving, setSaving] = useState(false);

  // Filter: only ORs with at least 1 active back_order anomaly
  const rows = useMemo(() => {
    return allRows.filter(or => {
      const lignes = Array.isArray(or.lignes_json) ? or.lignes_json : [];
      return lignes.some(l => l.anomalie_active?.type_anomalie === 'back_order');
    });
  }, [allRows]);

  // Société affichée : nom de concession mappé (societe_nom) ou fallback code brut.
  const societeLabel = (r) => r.societe_nom || r.societe;
  const societes = [...new Set(rows.map(societeLabel).filter(Boolean))].sort();

  const filtered = useMemo(() => {
    return rows.filter(r => {
      const label = societeLabel(r);
      if (filtreSociete && label !== filtreSociete) return false;
      if (!filtreSociete && user?.concession_nom && label !== user.concession_nom) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        (r.num_or      || '').toLowerCase().includes(q) ||
        (r.nom_client  || '').toLowerCase().includes(q) ||
        immatCorrespond(r.immat, search) ||
        (r.modele      || '').toLowerCase().includes(q)
      );
    });
  }, [rows, search, filtreSociete]);

  // Count total back order pieces
  const totalBackOrder = useMemo(() => {
    return filtered.reduce((sum, or) => {
      const lignes = Array.isArray(or.lignes_json) ? or.lignes_json : [];
      return sum + lignes.filter(l => l.anomalie_active?.type_anomalie === 'back_order').length;
    }, 0);
  }, [filtered]);

  // Update modal when rows change
  useEffect(() => {
    if (modal) {
      const updated = allRows.find(r => r.num_or === modal.num_or);
      if (updated) setModal(updated);
    }
  }, [allRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResolve = async (anomalieId) => {
    if (!modal) return;
    setSaving(true);
    try {
      await carnetCommandeApi.resolveAnomalie(anomalieId, {
        resolution_commentaire: resolveComment || null,
      });
      setResolveForm(null);
      setResolveComment('');
      if (reload) await reload();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la résolution');
    } finally {
      setSaving(false);
    }
  };

  const openModal = (or) => {
    setModal(or);
    setResolveForm(null);
    setResolveComment('');
  };

  // For the modal, only show back_order lines
  const modalBackOrderLines = modal
    ? (Array.isArray(modal.lignes_json) ? modal.lignes_json : []).filter(l => l.anomalie_active?.type_anomalie === 'back_order')
    : [];

  return (
    <div className="cc-wrap">
      {/* Stats */}
      <div className="cc-bo-stats">
        <span className="cc-bo-stat">{filtered.length} OR{filtered.length !== 1 ? 's' : ''}</span>
        <span className="cc-bo-stat-sep">·</span>
        <span className="cc-bo-stat">{totalBackOrder} pièce{totalBackOrder !== 1 ? 's' : ''} en back order</span>
      </div>

      {/* Recherche + filtres */}
      <div className="page-toolbar">
        <input
          type="text"
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className={`page-select${filtreSociete ? ' active-filter' : ''}`}
          value={filtreSociete}
          onChange={e => setFiltreSociete(e.target.value)}
        >
          <option value="">Toutes les sociétés</option>
          {societes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="cc-loading">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="cc-empty">Aucun OR avec des pièces en back order.</div>
      ) : (
        <div className="table-container">
          <table className="cc-table">
            <thead>
              <tr>
                <th>OR N°</th>
                <th>Immat.</th>
                <th>Marque</th>
                <th>Réceptionnaire</th>
                <th>Date service</th>
                <th>Nb pièces BO</th>
                <th>Statut livraison</th>
                <th>Société</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(or => {
                const lignes = Array.isArray(or.lignes_json) ? or.lignes_json : [];
                const nbBO = lignes.filter(l => l.anomalie_active?.type_anomalie === 'back_order').length;
                return (
                  <tr key={or.id} className="cc-row" onClick={() => openModal(or)}>
                    <td className="cc-or">
                      {or.num_or || '—'}
                      <br/><span className="cc-client-sub">{or.nom_client || ''}</span>
                    </td>
                    <td>{or.immat ? afficherImmat(or.immat) : '—'}</td>
                    <td>{or.marque || '—'}</td>
                    <td>{or.receptionnaire || '—'}</td>
                    <td>{formatDate(or.date_service)}</td>
                    <td className="cc-nb">
                      <span className="cc-bo-count-badge">{nbBO}</span>
                    </td>
                    <td>{statutBadge(or.statut_livraison_or)}</td>
                    <td title={or.societe || ''}>{or.societe_nom || or.societe || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modale détail back order */}
      {modal && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 className="cc-modal-title">OR {modal.num_or} — Back orders</h3>
                <div className="cc-modal-subtitle">
                  {modal.nom_client && <span>{modal.nom_client}</span>}
                  {modal.immat && <span> · {afficherImmat(modal.immat)}</span>}
                  {modal.date_service && <span> · {formatDate(modal.date_service)}</span>}
                  <span style={{ marginLeft: 8 }}>{statutBadge(modal.statut_livraison_or)}</span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setModal(null)}>✕</button>
            </div>

            <div className="modal-body">
              {modalBackOrderLines.length === 0 ? (
                <p className="cc-modal-empty">Aucune pièce en back order sur cet OR.</p>
              ) : (
                <div className="cc-modal-table-wrap">
                  <table className="cc-modal-table">
                    <thead>
                      <tr>
                        <th>Référence</th>
                        <th>Description</th>
                        <th>Fournisseur</th>
                        <th>Cdé</th>
                        <th>Qté livrée</th>
                        <th>Statut DMS</th>
                        <th>Commentaire BO</th>
                        <th>Signalé par</th>
                        <th>Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalBackOrderLines.map((l, i) => {
                        const a = l.anomalie_active;
                        const isResolveOpen = resolveForm === a?.id;
                        return (
                          <React.Fragment key={i}>
                            <tr>
                              <td className="cc-modal-ref">{l.reference || '—'}</td>
                              <td className="cc-modal-desc">{l.description || '—'}</td>
                              <td>{l.fournisseur || '—'}</td>
                              <td className="cc-modal-center">{l.cde_qty ?? 0}</td>
                              <td className="cc-modal-center">{l.qte_livree ?? 0}</td>
                              <td>{statutBadge(l.statut_livraison)}</td>
                              <td className="cc-bo-comment">{a?.commentaire || '—'}</td>
                              <td className="cc-anomalie-meta">{a?.created_by_name || '—'}</td>
                              <td className="cc-anomalie-meta">{a ? formatDate(a.created_at) : '—'}</td>
                              <td>
                                <button
                                  className="cc-btn-resolve"
                                  onClick={() => { setResolveForm(isResolveOpen ? null : a.id); setResolveComment(''); }}
                                  title="Résoudre (pièce reçue)"
                                >
                                  ✓ Résoudre
                                </button>
                              </td>
                            </tr>
                            {isResolveOpen && (
                              <tr className="cc-anomalie-form-row">
                                <td colSpan={10}>
                                  <div className="cc-anomalie-form">
                                    <span className="cc-anomalie-form-label">Résoudre le back order sur <strong>{l.reference}</strong> :</span>
                                    <input
                                      type="text"
                                      className="cc-anomalie-input"
                                      placeholder="Commentaire de résolution (optionnel)"
                                      value={resolveComment}
                                      onChange={e => setResolveComment(e.target.value)}
                                    />
                                    <button
                                      className="cc-btn-confirm-resolve"
                                      onClick={() => handleResolve(a.id)}
                                      disabled={saving}
                                    >
                                      {saving ? '…' : 'Confirmer'}
                                    </button>
                                    <button className="cc-btn-cancel" onClick={() => setResolveForm(null)}>Annuler</button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
